import {
  Badge,
  Callout,
  Hint,
  Panel,
  PanelTitle,
  Row,
  StatTable,
} from 'features/sentinelDesign/designStyles'
import { unscopedWarnings } from 'features/sentinelDesign/PlanRationale'
import type {
  SentinelPlan,
  SentinelPlanWarning,
} from 'features/sentinelDesign/types'
import { FC } from 'react'
import styled from 'styled-components'

const Narrative = styled.p`
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`

const Why = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.68rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export interface PlanSummaryProps {
  plan: SentinelPlan
  rationale?: Record<string, string>
  narrative?: string
  warnings?: SentinelPlanWarning[]
  /** Why this plan cannot be edited here. */
  frozenBecause: string
}

/**
 * The plan, read-only.
 *
 * Rendered wherever the plan is history rather than a draft: while the job that
 * is fitting it runs, and forever after on the finished sentinel. It reads
 * `plan.json` like everything else does, so what it shows is the plan the
 * backend holds rather than whatever this tab last had in memory.
 */
export const PlanSummary: FC<PlanSummaryProps> = ({
  plan,
  rationale,
  narrative,
  warnings,
  frozenBecause,
}) => {
  const topWarnings = unscopedWarnings(warnings)
  const rows: [string, string, string | undefined][] = [
    ['Target', plan.target || '—', rationale?.target],
    [
      'Drivers',
      plan.drivers.length ? plan.drivers.join(', ') : '—',
      rationale?.drivers,
    ],
    [
      'Window',
      `${plan.start_date || '—'} → ${plan.end_date || '—'}`,
      rationale?.start_date ?? rationale?.end_date,
    ],
    ['Methods', plan.methods.join(', ') || '—', rationale?.methods],
    ['Interval', `${plan.interval_minutes} min`, rationale?.interval_minutes],
    ['Gate scope', plan.gate_scope, rationale?.gate_scope],
  ]

  return (
    <Panel aria-label='Model plan'>
      <Row>
        <PanelTitle>Model plan</PanelTitle>
        <Badge>{plan.drivers.length} drivers</Badge>
        <Badge>{plan.methods.length} methods</Badge>
        <Badge>read-only</Badge>
      </Row>
      <Hint>{frozenBecause}</Hint>

      {narrative && <Narrative>{narrative}</Narrative>}

      {topWarnings.map((warning) => (
        <Callout
          key={`${warning.code ?? ''}${warning.message}`}
          $tone={warning.severity === 'error' ? 'error' : 'warning'}>
          {warning.code ? <strong>{warning.code}: </strong> : null}
          {warning.message}
        </Callout>
      ))}

      <StatTable>
        <tbody>
          {rows.map(([label, value, why]) => (
            <tr key={label}>
              <th scope='row'>{label}</th>
              <td>
                {label === 'Drivers' && plan.drivers.length ? (
                  <Tags>
                    {plan.drivers.map((driver) => (
                      <Badge key={driver}>{driver}</Badge>
                    ))}
                  </Tags>
                ) : (
                  value
                )}
                {why && <Why>{why}</Why>}
              </td>
            </tr>
          ))}
        </tbody>
      </StatTable>
    </Panel>
  )
}
